import { Button, Chip, Input } from '@emailforge/ui';

import { Icon } from '@/components/ui/icons';
import { Logo, Wordmark } from '@/components/ui/Logo';

type Swatch = { token: string; label: string; color: string; hex: string; dashed?: boolean };

const ACCENT_SWATCHES: Swatch[] = [
  { token: '--flame', label: 'primary', color: 'var(--flame)', hex: '#D96A3A' },
  { token: '--flame-2', label: 'pressed', color: 'var(--flame-2)', hex: '#BD5022' },
  { token: '--spark', label: 'success', color: 'var(--spark)', hex: '#4FB982' },
  { token: '--warn', label: 'caution', color: 'var(--warn)', hex: '#D4A24A' },
  { token: '--err', label: 'error', color: 'var(--err)', hex: '#C93A2B' },
  { token: '--ink', label: 'text', color: 'var(--ink)', hex: '#1C1916' },
];

const NEUTRAL_SWATCHES: Swatch[] = [
  { token: '--paper', label: 'primary bg', color: 'var(--paper)', hex: '#FBFAF8', dashed: true },
  { token: '--paper-2', label: 'panels', color: 'var(--paper-2)', hex: '#F3F1ED' },
  { token: '--paper-3', label: 'hover fills', color: 'var(--paper-3)', hex: '#EBE8E2' },
  { token: '--paper-4', label: 'stronger fills', color: 'var(--paper-4)', hex: '#DDD9D1' },
  { token: '--line', label: 'borders', color: 'var(--line)', hex: '#D3CFC7' },
  { token: '--ink-3', label: 'tertiary text', color: 'var(--ink-3)', hex: '#807B75' },
];

const PRINCIPLES = [
  {
    n: '01',
    t: 'Dense, not cramped',
    d: '13px base. 28px controls. Every pixel earns its row — but lines breathe.',
  },
  {
    n: '02',
    t: 'The system talks mono',
    d: 'Tokens, identifiers, timestamps, latencies — always Geist Mono, always ink-3.',
  },
  {
    n: '03',
    t: 'One loud color',
    d: 'Flame is reserved for state that matters: primary action, selection, live experiment.',
  },
];

export function DesignSystemScreen() {
  return (
    <div style={{ background: 'var(--paper)', minHeight: '100vh' }}>
      <header
        style={{
          padding: '28px 40px',
          borderBottom: '1px solid var(--line)',
          background: 'var(--paper)',
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <Wordmark sub />
        <div style={{ flex: 1 }} />
        <div
          className="mono"
          style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.08 }}
        >
          Design system · v0.1
        </div>
      </header>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '48px 40px 64px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, marginBottom: 8 }}>
          <Logo size={48} />
          <div>
            <h1
              className="display"
              style={{ fontSize: 44, letterSpacing: -0.025, lineHeight: 1, margin: 0 }}
            >
              Emailforge — <i style={{ color: 'var(--flame)' }}>design system</i>
            </h1>
            <div
              className="mono"
              style={{
                fontSize: 11,
                color: 'var(--ink-3)',
                letterSpacing: 0.08,
                textTransform: 'uppercase',
                marginTop: 6,
              }}
            >
              v0.1 · EmailOps for developers · pro-tool density
            </div>
          </div>
        </div>
        <p style={{ color: 'var(--ink-2)', fontSize: 14, maxWidth: 720, marginTop: 6, lineHeight: 1.55 }}>
          A warm, technical vocabulary for building and shipping emails at speed. A single display face for
          voice, Geist for the interface, Geist Mono for everything the system says about itself.
        </p>

        <Divider />

        <Section number="01" title="Palette">
          <SwatchGrid swatches={ACCENT_SWATCHES} large />
          <div style={{ height: 12 }} />
          <SwatchGrid swatches={NEUTRAL_SWATCHES} />
        </Section>

        <Divider />

        <Section number="02" title="Typography">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div className="mf-card" style={{ padding: 20 }}>
              <Label>display · Fraunces · italic for accent</Label>
              <div
                className="display"
                style={{ fontSize: 54, lineHeight: 1.0, letterSpacing: -0.025, marginTop: 8 }}
              >
                Emails that render <i style={{ color: 'var(--flame)' }}>everywhere</i>.
              </div>
              <div
                className="mono"
                style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 16 }}
              >
                used for hero moments · max 3 per page
              </div>
            </div>
            <div className="mf-card" style={{ padding: 20 }}>
              <Label>body · Geist · 400 / 500 / 600</Label>
              <div style={{ fontSize: 22, fontWeight: 550, marginTop: 10, letterSpacing: -0.005 }}>
                UI headings — 22 / 550
              </div>
              <div style={{ fontSize: 15, marginTop: 6 }}>
                Body copy — 15 / 400 — optimized for density in panels and inspectors.
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
                Caption — 12 / 400 / ink-3 — for meta and secondary info.
              </div>
              <div className="mono" style={{ fontSize: 11, marginTop: 10, color: 'var(--ink-2)' }}>
                mono · Geist Mono · identifiers, tokens, timestamps
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, marginTop: 14 }}>
            {[10, 11, 12, 13, 15, 22, 38].map((px) => (
              <div
                key={px}
                style={{
                  border: '1px solid var(--line-soft)',
                  borderRadius: 5,
                  padding: 12,
                  textAlign: 'center',
                }}
              >
                <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                  {px}px
                </div>
                <div style={{ fontSize: px, fontWeight: 500, marginTop: 4 }}>Aa</div>
              </div>
            ))}
          </div>
        </Section>

        <Divider />

        <Section number="03" title="Components">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div
              className="mf-card"
              style={{ padding: 20, background: 'var(--paper-2)' }}
            >
              <Label>buttons</Label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                <Button variant="primary">Publish</Button>
                <Button variant="flame">Fork template</Button>
                <Button>Send test</Button>
                <Button variant="ghost">Cancel</Button>
                <Button size="sm" variant="primary">
                  sm primary
                </Button>
                <Button size="sm">sm default</Button>
                <Button size="sm" icon>
                  <Icon.plus size={13} />
                </Button>
              </div>

              <div style={{ marginTop: 18 }}>
                <Label>chips</Label>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                <Chip variant="live">live</Chip>
                <Chip variant="draft">draft</Chip>
                <Chip variant="err">bounced</Chip>
                <Chip variant="flame">running</Chip>
                <Chip>transactional</Chip>
              </div>

              <div style={{ marginTop: 18 }}>
                <Label>inputs</Label>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320, marginTop: 10 }}>
                <Input placeholder="Search templates…" />
                <Input
                  className="mono"
                  defaultValue="{{first_name}}"
                  style={{ color: 'var(--flame-2)' }}
                  readOnly
                />
              </div>
            </div>

            <div className="mf-card" style={{ padding: 20 }}>
              <Label>card · KPI</Label>
              <div
                className="mf-card"
                style={{ padding: '14px 16px', marginTop: 10 }}
              >
                <div
                  className="mono"
                  style={{
                    fontSize: 10,
                    color: 'var(--ink-3)',
                    letterSpacing: 0.08,
                    textTransform: 'uppercase',
                  }}
                >
                  Open rate · 30d
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
                  <div className="mono" style={{ fontSize: 22, fontWeight: 550 }}>
                    47.2%
                  </div>
                  <div className="mono" style={{ fontSize: 11, color: 'oklch(0.45 0.14 148)' }}>
                    +2.1pt
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 18 }}>
                <Label>block tile (editor)</Label>
              </div>
              <div
                style={{
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  padding: 10,
                  background: 'var(--paper)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  width: 200,
                  marginTop: 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 4,
                      background: 'var(--paper-3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon.image size={12} />
                  </div>
                  <span style={{ fontSize: 11.5, fontWeight: 500 }}>Hero</span>
                </div>
                <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                  Image + heading
                </div>
              </div>

              <div style={{ marginTop: 18 }}>
                <Label>logo · wordmark</Label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 12 }}>
                <Logo size={32} />
                <Wordmark size={14} />
              </div>
            </div>
          </div>
        </Section>

        <Divider />

        <Section number="04" title="Principles">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
            {PRINCIPLES.map((p) => (
              <div key={p.n} className="mf-card" style={{ padding: 18 }}>
                <div className="mono" style={{ fontSize: 10, color: 'var(--flame)' }}>
                  {p.n}
                </div>
                <div className="display" style={{ fontSize: 22, letterSpacing: -0.01, marginTop: 6 }}>
                  {p.t}
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 6, lineHeight: 1.5 }}>
                  {p.d}
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}

function SwatchGrid({ swatches, large = false }: { swatches: Swatch[]; large?: boolean }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 }}>
      {swatches.map((s) => (
        <div
          key={s.token}
          style={{ border: '1px solid var(--line)', borderRadius: 6, overflow: 'hidden' }}
        >
          <div
            style={{
              height: large ? 76 : 46,
              background: s.color,
              border: s.dashed ? '1px dashed var(--line)' : 'none',
            }}
          />
          <div style={{ padding: '8px 10px' }}>
            <div className="mono" style={{ fontSize: 11.5, fontWeight: 550 }}>
              {s.token}
            </div>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 2 }}>
              {s.label} · {s.hex}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Section({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div
        className="mono"
        style={{
          fontSize: 10,
          color: 'var(--ink-3)',
          letterSpacing: 0.1,
          textTransform: 'uppercase',
          marginBottom: 14,
        }}
      >
        {number} · {title}
      </div>
      {children}
    </section>
  );
}

function Divider() {
  return <div style={{ height: 1, background: 'var(--line)', margin: '36px 0' }} />;
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mono"
      style={{
        fontSize: 10,
        color: 'var(--ink-3)',
        letterSpacing: 0.08,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </div>
  );
}
